// frontend/src/App.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter/dist/esm";
import { vscDarkPlus, coy } from "react-syntax-highlighter/dist/esm/styles/prism";
import "./styles/index.css";
import { logoutIfExpired } from "./utils/auth";
import { chatAPI, userAPI, modelAPI } from "./utils/api";
import ApiErrorHandler from "./components/ApiErrorHandler";
import ModelSelector from "./components/ModelSelector";
import "./styles/ApiErrorHandler.css"

function App({ user, setUser }) {
  const [theme, setTheme] = useState(
    () => localStorage.getItem("accessai-theme") || "light"
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeModes, setActiveModes] = useState({
    codeMode: false,
    auditMode: false,
    emotionDetection: false,
    criticalThinking: false,
    simulationMode: false,
    aiLiteracy: false,
    collaborationMode: false,
  });
  const [tone, setTone] = useState("friendly");
  const [responseLength, setResponseLength] = useState("concise");
  const [projects, setProjects] = useState(() => {
    const saved = localStorage.getItem("accessai-projects");
    return saved ? JSON.parse(saved) : [];
  });
  const [currentProject, setCurrentProject] = useState(null);
  const [newProjectName, setNewProjectName] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [credits, setCredits] = useState(user ? Infinity : 5);
  const [isRecording, setIsRecording] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [ttsSupported, setTtsSupported] = useState(false);
  const [speechError, setSpeechError] = useState("");
  const [leaderboard, setLeaderboard] = useState([]);
  const [points, setPoints] = useState(user ? user.points : 0);
  const [level, setLevel] = useState(user ? user.level : 1);
  const [progress, setProgress] = useState(0);
  const [fullCodeView, setFullCodeView] = useState(null);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(user?.preferredModel || "xiaomi/mimo-v2-flash:free");
  const [modelStats, setModelStats] = useState({});
  const [loadingModels, setLoadingModels] = useState(false);

  const mediaRecorderRef = useRef(null);
  const messagesEndRef = useRef(null);
  const sidebarRef = useRef(null);
  const recognitionRef = useRef(null);
  const navigate = useNavigate();

  // Toast Helper
  const showToast = (message, type = "info") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 4000);
  };

  const closeToast = () => setToast({ show: false, message: "", type: "" });

  // Theme setup - Persist on reload
  useEffect(() => {
    const savedTheme = localStorage.getItem("accessai-theme") || "light";
    setTheme(savedTheme);
    document.documentElement.setAttribute("data-theme", savedTheme);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("accessai-theme", theme);
  }, [theme]);

  // Project persistence
  useEffect(() => {
    localStorage.setItem("accessai-projects", JSON.stringify(projects));
  }, [projects]);

  // Restore project messages when switching
  useEffect(() => {
    if (currentProject) {
      const project = projects.find(p => p.name === currentProject);
      setMessages(project ? project.messages : []);
    } else {
      setMessages([]);
    }
  }, [currentProject, projects]);

  // Token expiration checking
  useEffect(() => {
    const checkTokenInterval = setInterval(() => {
      if (logoutIfExpired(user, setUser)) {
        setApiError({
          status: 401,
          message: "Your session has expired. Please login again."
        });
      }
    }, 60000);
    return () => clearInterval(checkTokenInterval);
  }, [user, setUser]);

  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Initial setup
  useEffect(() => {
    if (logoutIfExpired(user, setUser)) {
      setApiError({
        status: 401,
        message: "Your session has expired. Please login again."
      });
    }

    // Basic Speech Recog Setup
    if ("SpeechRecognition" in window || "webkitSpeechRecognition" in window) {
      setSpeechSupported(true);
      recognitionRef.current = new (window.SpeechRecognition ||
        window.webkitSpeechRecognition)();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = "en-US";
      recognitionRef.current.onresult = (event) =>
        setInput(event.results[0][0].transcript);
      recognitionRef.current.onerror = (event) => {
        setSpeechError(`Error: ${event.error}`);
        setIsRecording(false);
      };
      recognitionRef.current.onend = () => setIsRecording(false);
    }

    if ("speechSynthesis" in window) setTtsSupported(true);
    fetchLeaderboard();
    setProgress(Math.min(100, Math.floor(((points % 100) / 100) * 100)));
    
    if (user) {
      fetchAvailableModels();
    }
  }, [points, user, setUser]);

  // Fetch leaderboard
  const fetchLeaderboard = async () => {
    try {
      const response = await userAPI.getLeaderboard({ limit: 5 });
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      console.error("Leaderboard error", error);
    }
  };

  // Fetch available models
  const fetchAvailableModels = async () => {
    if (!user) return;
    
    setLoadingModels(true);
    try {
      const response = await modelAPI.getAvailableModels();
      
      if (response.data.success) {
        setAvailableModels(response.data.models || []);
        setModelStats(response.data.modelStats || {});
        
        if (response.data.userPreferredModel) {
          setSelectedModel(response.data.userPreferredModel);
        }
      }
    } catch (error) {
      console.error("Error fetching models:", error);
      showToast("Failed to load AI models", "error");
    } finally {
      setLoadingModels(false);
    }
  };

  // Update user's preferred model
  const updateModel = async (modelId) => {
    if (!user) return;
    
    try {
      const response = await modelAPI.updateModel(modelId);
      
      if (response.data.success) {
        setSelectedModel(modelId);
        showToast(`Model switched to ${response.data.modelName}`, "success");
        
        if (setUser) {
          setUser({
            ...user,
            preferredModel: modelId
          });
        }
        
        fetchAvailableModels();
      }
    } catch (error) {
      console.error("Error updating model:", error);
      setApiError({
        status: error.response?.status,
        message: error.response?.data?.details || "Failed to update model",
        data: error.response?.data
      });
    }
  };

  // Handle model switch from error handler
  const handleSwitchModel = async (modelName) => {
    if (!user) return;
    
    // Find model ID by display name
    const model = availableModels.find(m => 
      m.name === modelName || m.displayName === modelName
    );
    
    if (model) {
      await updateModel(model.id);
      setApiError(null);
    }
  };

  const toggleTheme = () =>
    setTheme((prev) => {
      const newTheme = prev === "light" ? "dark" : "light";
      localStorage.setItem("accessai-theme", newTheme);
      return newTheme;
    });

  const toggleMode = (mode) =>
    setActiveModes((prev) => ({ ...prev, [mode]: !prev[mode] }));

  const createProject = () => {
    if (newProjectName.trim()) {
      const projectName = newProjectName.trim();
      const existingProject = projects.find(p => p.name === projectName);
      
      if (existingProject) {
        showToast("Project with this name already exists", "warning");
        return;
      }
      
      const project = {
        name: projectName,
        messages: [],
        createdAt: new Date().toISOString(),
      };
      setProjects((prev) => [...prev, project]);
      setNewProjectName("");
      setCurrentProject(project.name);
      showToast("Project created successfully", "success");
    }
  };

  const switchProject = (projectName) => {
    if (currentProject) {
      // Save current project messages
      setProjects((prev) =>
        prev.map((p) => 
          p.name === currentProject ? { ...p, messages } : p
        )
      );
    }
    setCurrentProject(projectName);
    const project = projects.find((p) => p.name === projectName);
    setMessages(project ? project.messages : []);
  };

  // Handle chat submission
  const handleSubmit = async (userInput) => {
    if (!userInput.trim()) return;
    
    if (user && logoutIfExpired(user, setUser)) {
      setShowSignupPrompt(true);
      return;
    }

    const newMessage = {
      role: "user",
      content: userInput,
      timestamp: new Date().toLocaleTimeString(),
      id: Date.now(),
    };

    const updatedMessages = [...messages, newMessage];
    setMessages(updatedMessages);
    setInput("");
    setLoading(true);

    if (!user && credits <= 0) {
      setShowSignupPrompt(true);
      setLoading(false);
      return;
    }

    let enhancedPrompt = userInput;
    const modeInstructions = Object.keys(activeModes)
      .filter((k) => activeModes[k])
      .map((k) => k.replace(/([A-Z])/g, " $1"));
    
    if (modeInstructions.length)
      enhancedPrompt = `${modeInstructions.join(". ")}. ${enhancedPrompt}`;

    try {
      const response = await chatAPI.sendMessage({
        messages: updatedMessages,
        input: enhancedPrompt,
        credits,
        model: selectedModel,
      });

      if (response.data.success) {
        const botMessage = {
          role: "assistant",
          content: response.data.response,
          timestamp: new Date().toLocaleTimeString(),
          id: Date.now() + 1,
          model: response.data.modelDisplayName || "AI Model",
          remainingRequests: response.data.remainingRequests,
          usedToday: response.data.usedToday,
          dailyLimit: response.data.dailyLimit,
        };
        
        const finalMessages = [...updatedMessages, botMessage];
        setMessages(finalMessages);
        
        // Update project with new messages
        if (currentProject) {
          setProjects(prev =>
            prev.map(p =>
              p.name === currentProject
                ? { ...p, messages: finalMessages }
                : p
            )
          );
        }
        
        if (!user) {
          setCredits(response.data.credits);
        } else {
          setPoints((prev) => {
            const newPoints = prev + 10;
            if (newPoints >= level * 100) setLevel((l) => l + 1);
            return newPoints;
          });
          
          try {
            await userAPI.updatePoints(10, 'chat_interaction');
          } catch (e) {
            console.error(e);
          }
          
          fetchAvailableModels();
        }
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        if (user) {
          logoutIfExpired(user, setUser);
        }
        setApiError({
          status: err.response?.status,
          message: "Session expired. Please login again.",
          data: err.response?.data
        });
      } else if (err.response?.status === 429) {
        setApiError({
          status: err.response?.status,
          message: err.response?.data?.details || err.message,
          data: err.response?.data
        });
        if (user) {
          fetchAvailableModels();
        }
      } else {
        setApiError({
          status: err.response?.status,
          message: "Failed to get response. Please try again.",
          data: err.response?.data
        });
      }
      
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Unable to process request at the moment.",
          isError: true,
          id: Date.now() + 1,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const toggleRecording = () => {
    if (!speechSupported) return;
    if (isRecording) recognitionRef.current.stop();
    else {
      setSpeechError("");
      recognitionRef.current.start();
      setIsRecording(true);
    }
  };

  const speakText = (text) => {
    if (ttsSupported) {
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 0.8;
      window.speechSynthesis.speak(u);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast("Copied to clipboard!", "success");
    } catch (e) {
      console.error(e);
    }
  };

  const languages = [
    { name: "JavaScript", icon: "🟨", path: "javascript" },
    { name: "Python", icon: "🐍", path: "python" },
    { name: "Java", icon: "☕", path: "java" },
    { name: "C++", icon: "➕", path: "cpp" },
    { name: "Solidity", icon: "🔗", path: "solidity" },
  ];

  return (
    <div className="min-h-screen bg-background text-text-primary transition-colors duration-300 font-sans">
      {/* Header - Responsive */}
      <header className="sticky top-0 z-50 w-full bg-surface/90 backdrop-blur-md border-b border-border shadow-sm px-3 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-surface-elevated text-text-primary lg:hidden"
            aria-label="Menu"
          >
            {sidebarOpen ? "✕" : "☰"}
          </button>
          <h1 className="text-lg md:text-xl lg:text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent whitespace-nowrap">
            AccessAI
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {/* Mobile DSA Button */}
          <button
            onClick={() => navigate("/dsa")}
            className="xs:hidden px-2.5 py-1 bg-secondary text-white rounded-lg text-xs font-medium hover:bg-secondary-hover transition-colors"
          >
            DSA
          </button>
          
          {user ? (
            <>
              <div className="hidden sm:flex flex-col items-end text-xs">
                <span className="font-semibold text-primary">
                  Level {level}
                </span>
                <span className="text-text-secondary">{points} XP</span>
              </div>
              <button
                onClick={() => navigate("/dsa")}
                className="hidden xs:block px-2.5 py-1 bg-secondary text-white rounded-lg text-xs font-medium hover:bg-secondary-hover transition-colors"
              >
                DSA Learning
              </button>
              <button
                onClick={() => {
                  setUser(null);
                  localStorage.removeItem("accessai-user");
                  localStorage.removeItem("accessai-projects");
                  navigate("/");
                }}
                className="text-xs text-error hover:text-error-light font-medium px-2"
              >
                Logout
              </button>
            </>
          ) : (
            <div className="flex gap-1.5">
              <button
                onClick={() => navigate("/login")}
                className="text-xs font-medium hover:text-primary transition-colors px-2"
              >
                Login
              </button>
              <button
                onClick={() => navigate("/signup")}
                className="px-2.5 py-1 bg-primary text-white rounded-lg text-xs hover:bg-primary-hover transition-colors"
              >
                Signup
              </button>
            </div>
          )}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full hover:bg-surface-elevated transition-colors text-lg"
            aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
          >
            {theme === "light" ? "🌙" : "☀️"}
          </button>
        </div>
      </header>

      <div className="flex h-[calc(100vh-64px)] overflow-hidden">
        {/* Sidebar - Responsive */}
        <aside
          ref={sidebarRef}
          className={`
            fixed lg:static inset-y-0 left-0 z-50 w-72 bg-surface border-r border-border
            transform transition-transform duration-300 ease-in-out overflow-y-auto
            ${
              sidebarOpen
                ? "translate-x-0"
                : "-translate-x-full lg:translate-x-0"
            }
            mt-16 lg:mt-0
          `}
        >
          <div className="p-3 md:p-4 space-y-4 md:space-y-6">
            {/* Model Selector - Responsive */}
            {user && (
              <div className="px-1">
                <ModelSelector
                  user={user}
                  selectedModel={selectedModel}
                  onModelChange={setSelectedModel}
                  onModelStatsUpdate={setModelStats}
                />
              </div>
            )}
            
            {/* AI Modes - Responsive */}
            <div>
              <h3 className="text-xs font-bold text-text-secondary uppercase tracking-wider mb-2">
                AI Modes
              </h3>
              <div className="space-y-1.5">
                {Object.entries(activeModes).map(([mode, active]) => (
                  <div
                    key={mode}
                    onClick={() => toggleMode(mode)}
                    className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                      active
                        ? "bg-primary/10 text-primary"
                        : "hover:bg-surface-elevated text-text-secondary"
                    }`}
                  >
                    <span className="text-xs font-medium truncate">
                      {mode
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (str) => str.toUpperCase())}
                    </span>
                    <div
                      className={`w-7 h-3.5 rounded-full relative transition-colors ${
                        active ? "bg-primary" : "bg-gray-300"
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 w-2.5 h-2.5 bg-white rounded-full shadow-sm transition-transform ${
                          active ? "left-3.5" : "left-0.5"
                        }`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Settings - Responsive */}
            <div>
              <h3 className="text-xs font-bold text-text-secondary uppercase tracking-wider mb-2">
                Settings
              </h3>
              <div className="space-y-2">
                <select
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="input-field text-xs"
                >
                  <option value="friendly">Friendly Tone</option>
                  <option value="professional">Professional</option>
                  <option value="humorous">Humorous</option>
                </select>
                <select
                  value={responseLength}
                  onChange={(e) => setResponseLength(e.target.value)}
                  className="input-field text-xs"
                >
                  <option value="concise">Concise</option>
                  <option value="detailed">Detailed</option>
                  <option value="comprehensive">Comprehensive</option>
                </select>
              </div>
            </div>
            
            {/* Projects - Responsive */}
            <div>
              <h3 className="text-xs font-bold text-text-secondary uppercase tracking-wider mb-2">
                Projects
              </h3>
              <div className="flex gap-1.5 mb-2.5">
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="New Project..."
                  className="input-field text-xs flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && createProject()}
                />
                <button
                  onClick={createProject}
                  className="px-2.5 bg-primary text-white rounded-lg text-lg hover:bg-primary-hover"
                >
                  +
                </button>
              </div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {projects.map((p) => (
                  <div
                    key={p.name}
                    onClick={() => switchProject(p.name)}
                    className={`p-1.5 rounded-lg cursor-pointer text-xs truncate flex justify-between items-center ${
                      currentProject === p.name
                        ? "bg-primary/10 text-primary font-medium"
                        : "hover:bg-surface-elevated text-text-secondary"
                    }`}
                  >
                    <span className="truncate">{p.name}</span>
                    <span className="text-[10px] text-text-muted">
                      {p.messages?.length || 0} msgs
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* Sidebar Overlay */}
        {sidebarOpen && (
          <div
            className="sidebar-overlay lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content - Responsive */}
        <main className="flex-1 flex flex-col h-full overflow-hidden relative">
          {/* Gamification Bar - Responsive */}
          <div className="bg-surface border-b border-border p-2.5 flex items-center justify-between shrink-0">
            <div className="flex-1 mr-3">
              <div className="flex justify-between text-[10px] md:text-xs mb-1 font-medium">
                <span>Progress (Lvl {level})</span>
                <span>
                  {points} / {level * 100} XP
                </span>
              </div>
              <div className="h-1.5 md:h-2 bg-border rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-success to-primary transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
            <div className="text-[10px] md:text-xs text-text-secondary hidden xs:block">
              Top: {leaderboard[0]?.username?.substring(0, 8) || "N/A"}
            </div>
          </div>

          {/* Chat Area - Responsive */}
          <div
            className="flex-1 overflow-y-auto p-3 md:p-4 space-y-3 md:space-y-4 scroll-smooth"
            role="log"
          >
            {messages.length === 0 && (
              <div className="space-y-6 mb-6 animate-fade-in">
                <div className="text-center py-6">
                  <h2 className="text-xl md:text-2xl font-bold mb-2">
                    Welcome to AccessAI
                  </h2>
                  <p className="text-text-secondary text-sm md:text-base">
                    Select a language to start learning or ask a question below.
                  </p>
                  
                  {/* REMOVED the model display section here */}
                </div>
                
                <div className="grid grid-cols-2 xs:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-3">
                  {languages.map((lang) => (
                    <div
                      key={lang.path}
                      onClick={() =>
                        user
                          ? navigate(`/learn/${lang.path}`)
                          : showToast("Please login first", "warning")
                      }
                      className="bg-surface border border-border p-3 rounded-xl text-center hover:border-primary hover:-translate-y-1 transition-all cursor-pointer shadow-sm"
                    >
                      <div className="text-2xl md:text-3xl mb-1 md:mb-2">{lang.icon}</div>
                      <div className="font-medium text-xs md:text-sm truncate">{lang.name}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div
                key={msg.id}
                className={`flex ${
                  msg.role === "user" ? "justify-end" : "justify-start"
                } animate-fade-in`}
              >
                <div
                  className={`max-w-[90%] sm:max-w-[85%] md:max-w-[80%] lg:max-w-[70%] rounded-2xl px-4 py-3 shadow-sm ${
                    msg.role === "user"
                      ? "bg-primary text-white rounded-tr-sm"
                      : msg.isError
                      ? "bg-error-light text-error border border-error"
                      : "bg-surface border border-border rounded-tl-sm"
                  }`}
                >
                  <ReactMarkdown
                    components={{
                      code({ node, inline, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || "");
                        return !inline && match ? (
                          <div className="my-2 md:my-3 rounded-lg overflow-hidden border border-border/50 shadow-sm">
                            <div className="bg-gray-800 text-gray-400 text-[10px] md:text-xs px-2 md:px-3 py-1 flex justify-between items-center">
                              <span className="truncate">{match[1]}</span>
                              <div className="flex gap-1 md:gap-2">
                                <button
                                  onClick={() =>
                                    copyToClipboard(String(children))
                                  }
                                  className="hover:text-white text-xs"
                                >
                                  Copy
                                </button>
                                <button
                                  onClick={() =>
                                    setFullCodeView(String(children))
                                  }
                                  className="hover:text-white text-xs"
                                >
                                  Expand
                                </button>
                              </div>
                            </div>
                            <div className="max-h-48 md:max-h-64 overflow-auto">
                              <SyntaxHighlighter
                                style={theme === "dark" ? vscDarkPlus : coy}
                                language={match[1]}
                                PreTag="div"
                                customStyle={{ 
                                  margin: 0, 
                                  borderRadius: 0,
                                  fontSize: '0.75rem',
                                  padding: '0.5rem'
                                }}
                                {...props}
                              >
                                {String(children).replace(/\n$/, "")}
                              </SyntaxHighlighter>
                            </div>
                          </div>
                        ) : (
                          <code
                            className={`${className} bg-black/10 dark:bg-white/10 px-1 py-0.5 rounded text-[10px] md:text-xs font-mono`}
                            {...props}
                          >
                            {children}
                          </code>
                        );
                      },
                    }}
                  >
                    {msg.content}
                  </ReactMarkdown>
                  <div
                    className={`text-[9px] md:text-[10px] mt-1.5 md:mt-2 flex justify-between items-center opacity-70 ${
                      msg.role === "user" ? "text-blue-100" : "text-text-muted"
                    }`}
                  >
                    <div className="flex items-center gap-1 md:gap-2 truncate">
                      <span>{msg.timestamp}</span>
                      {msg.model && (
                        <span className="px-1 md:px-1.5 py-0.5 bg-surface/50 rounded text-[8px] md:text-xs truncate">
                          {msg.model.substring(0, 10)}
                        </span>
                      )}
                    </div>
                    {msg.role === "assistant" && !msg.isError && (
                      <div className="flex gap-1 md:gap-2 ml-2 md:ml-4">
                        <button
                          onClick={() => copyToClipboard(msg.content)}
                          className="hover:opacity-100"
                          aria-label="Copy message"
                        >
                          📋
                        </button>
                        <button
                          onClick={() => speakText(msg.content)}
                          disabled={!ttsSupported}
                          className="hover:opacity-100 disabled:opacity-30"
                          aria-label="Read aloud"
                        >
                          🔊
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-surface border border-border px-3 md:px-4 py-2 md:py-3 rounded-2xl rounded-tl-sm flex items-center gap-1.5 md:gap-2">
                  <div
                    className="w-1.5 h-1.5 md:w-2 md:h-2 bg-primary rounded-full animate-bounce"
                    style={{ animationDelay: "0s" }}
                  />
                  <div
                    className="w-1.5 h-1.5 md:w-2 md:h-2 bg-primary rounded-full animate-bounce"
                    style={{ animationDelay: "0.2s" }}
                  />
                  <div
                    className="w-1.5 h-1.5 md:w-2 md:h-2 bg-primary rounded-full animate-bounce"
                    style={{ animationDelay: "0.4s" }}
                  />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area - Responsive */}
          <div className="p-3 md:p-4 bg-surface/50 border-t border-border backdrop-blur-sm">
            <div className="max-w-4xl mx-auto flex gap-1.5 md:gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit(input)}
                placeholder={
                  isRecording ? "Listening..." : "Ask your coding tutor..."
                }
                disabled={loading}
                className="flex-1 input-field shadow-sm text-sm md:text-base"
              />
              <button
                onClick={toggleRecording}
                disabled={!speechSupported || loading}
                className={`p-2.5 md:p-3 rounded-lg border border-border transition-all ${
                  isRecording
                    ? "bg-error text-white animate-pulse"
                    : "bg-surface hover:bg-surface-elevated text-text-secondary"
                }`}
                aria-label={isRecording ? "Stop recording" : "Start recording"}
              >
                {isRecording ? "⏹" : "🎤"}
              </button>
              <button
                onClick={() => handleSubmit(input)}
                disabled={loading || !input.trim()}
                className="btn-primary py-2 px-4 md:px-6 shadow-md text-sm md:text-base"
              >
                Send
              </button>
            </div>
            {speechError && (
              <div className="text-[10px] md:text-xs text-error text-center mt-1.5">
                {speechError}
              </div>
            )}
            {!user && (
              <div className="text-[10px] md:text-xs text-center mt-1.5 text-text-muted">
                Guest Credits: {credits}/5
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modals & Toasts - Responsive */}
      {showSignupPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 md:p-4">
          <div className="bg-surface p-4 md:p-6 lg:p-8 rounded-2xl shadow-2xl max-w-xs xs:max-w-sm w-full text-center border border-border animate-fade-in">
            <h3 className="text-lg md:text-xl font-bold mb-2">Out of Credits</h3>
            <p className="text-text-secondary text-sm md:text-base mb-4 md:mb-6">
              Sign up for free to continue your learning journey without limits!
            </p>
            <div className="flex flex-col gap-2 md:gap-3">
              <button
                onClick={() => {
                  setShowSignupPrompt(false);
                  navigate("/signup");
                }}
                className="btn-primary w-full text-sm md:text-base"
              >
                Sign Up Now
              </button>
              <button
                onClick={() => setShowSignupPrompt(false)}
                className="text-xs md:text-sm text-text-muted hover:text-text-primary"
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      {fullCodeView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-2 md:p-4 lg:p-8">
          <div className="bg-[#1e1e1e] w-full max-w-4xl lg:max-w-6xl h-full max-h-[90vh] rounded-xl overflow-hidden flex flex-col shadow-2xl">
            <div className="flex justify-between items-center p-2 md:p-4 bg-[#252526] border-b border-[#333]">
              <span className="text-gray-300 text-xs md:text-sm font-mono">Code View</span>
              <button
                onClick={() => setFullCodeView(null)}
                className="text-gray-400 hover:text-white text-lg md:text-xl"
                aria-label="Close code view"
              >
                ✕ Close
              </button>
            </div>
            <pre className="flex-1 overflow-auto p-2 md:p-4 text-xs md:text-sm font-mono text-gray-200">
              <code>{fullCodeView}</code>
            </pre>
          </div>
        </div>
      )}

      {toast.show && (
        <div
          className={`fixed bottom-4 right-4 z-50 px-3 py-2 md:px-4 md:py-3 rounded-lg shadow-lg border animate-fade-in flex items-center gap-2 md:gap-3 max-w-xs xs:max-w-sm ${
            toast.type === "error"
              ? "bg-error-light border-error text-error-dark"
              : toast.type === "success"
              ? "bg-success-light border-success text-success-dark"
              : "bg-surface border-primary text-primary"
          }`}
        >
          <span className="text-xs md:text-sm">{toast.message}</span>
          <button onClick={closeToast} className="opacity-60 hover:opacity-100 text-sm md:text-base" aria-label="Close notification">
            ✕
          </button>
        </div>
      )}

      {/* Global API Error Handler */}
      <ApiErrorHandler
        error={apiError}
        onClose={() => setApiError(null)}
        onSwitchModel={handleSwitchModel}
      />
    </div>
  );
}

export default App;